import { Component, OnInit, Input } from '@angular/core';
import { Router } from '@angular/router';
import { BsModalService, BsModalRef } from 'ngx-bootstrap/modal';

import { ToastrService } from 'ngx-toastr';
import moment from 'moment';

import { REQUEST_PRIORITIES, US_STATES } from 'app/app.constants';
import { getErrorMessage } from 'app/_utils/helpers';

import {
  AuthenticationService,
  CompanyService,
  ClientService,
  MeetingTypeService,
  TopicService,
  DropdownService,
  RequestFormService,
  VendorService,
  RequestService,
  PreRequestFormService,
  EquipmentService,
} from 'app/_services';

import {
  Client,
  Request,
  MeetingType,
  Topic,
  Speaker,
  Vendor,
  PreRequestForm,
  RequestForm,
} from 'app/_models';

import { BaseComponent } from 'app/_components/base.component';
import { CallPlanSearchModalComponent } from 'app/_components/call_plan-search-modal/call_plan-search-modal.component';
import { CallPlanNewModalComponent } from '../call_plan-search-modal/call_plan-new-modal.component';

import { DatatablesUtils } from 'app/_utils/datatables';

import { moveIn } from '../../router.animations';

declare const $: any;

@Component({
  selector: 'app-request-form',
  templateUrl: './request-form.component.html',
  styleUrls: ['./request-form.component.css'],
  animations: [moveIn()],
})
export class RequestFormComponent extends BaseComponent implements OnInit {
  @Input() model: Request;
  @Input() returnUrl: string;
  @Input() returnUrlParams: any = {};
  @Input() previewEvent: any;

  steps: any[] = [];

  preRequestForm: PreRequestForm;

  states: any[];
  professions: any[];
  degrees: any[];
  specialties: any[];

  meetingTypes: MeetingType[];
  meetingType: MeetingType;

  requestForms: RequestForm[];

  priorities: any[];

  clients: Client[];
  dtOptionsClients: DataTables.Settings = {};
  client: Client;
  clientAreas: any[] = [];
  selectedArea: any;

  dedicatedQuestionTypes: string[];

  speakers: Speaker[];
  speakersExtended: any;
  dtOptionsSpeakers: DataTables.Settings = {};
  selectedSpeakers: Speaker[] = [];

  topics: Topic[];
  topic: Topic;

  minDate: Date | null;
  maxDate: Date | null;
  datePickerDisabled: Boolean;
  timezones: any[];
  mealTypeOptions: any[] = [];
  event_time: Date;
  end_time: Date;
  selectedEventDates: any[];

  speakerEvents: any[] = [];

  venues: Vendor[];
  dtOptionsVenues: DataTables.Settings = {};
  /** Exact US state filter for venue DataTable (blank = all states). */
  venueFilterState = '';
  selectedVenues: Vendor[];

  caterers: Vendor[];
  dtOptionsCaterers: DataTables.Settings = {};
  caterer: Vendor;

  avEquipments: any[];
  selectedAvEquipments: any[];

  bsAttendeeSearchModalRef: BsModalRef;
  bsAttendeeNewModalRef: BsModalRef;

  toLastStep: boolean;
  priorityAutomaticallySelected: boolean;

  @Input() previewModalFlag: boolean;

  private dedupeMealTypeOptions(options: any[]): any[] {
    if (!Array.isArray(options)) return [];
    const seen = new Set<string>();
    const deduped: any[] = [];
    options.forEach((opt: any) => {
      const value = String(opt?.value ?? '');
      const label = String(opt?.label ?? value);
      const key = `${value}|${label}`;
      if (!seen.has(key)) {
        seen.add(key);
        deduped.push(opt);
      }
    });
    return deduped;
  }

  constructor(
    router: Router,
    authenticationService: AuthenticationService,
    companyService: CompanyService,
    private clientService: ClientService,
    private meetingTypeService: MeetingTypeService,
    private topicService: TopicService,
    private dropdownService: DropdownService,
    private requestFormService: RequestFormService,
    private preRequestFormService: PreRequestFormService,
    private vendorService: VendorService,
    private requestService: RequestService,
    private toastr: ToastrService,
    private modalService: BsModalService,
    private equipmentService: EquipmentService,
  ) {
    super(router, authenticationService, companyService);
    this.toLastStep = false;
  }

  ngOnInit() {
    this.dedicatedQuestionTypes = [];
    this.dropdownService.getDropdowns('CountryState,profession,attendee_degree,attendee_specialty').subscribe((lists) => {
      this.states = lists.CountryState;
      this.degrees = lists.attendee_degree;
      this.specialties = lists.attendee_specialty;
      this.professions = lists.profession;
    });
    this.dropdownService.getDropdownsForCompany(this.getCurrentCompanyId(), 'Timezone,RequestFormQuestionType,meal_type').subscribe((list) => {
      this.timezones = list.Timezone;
      this.mealTypeOptions = this.dedupeMealTypeOptions(list.meal_type || []);
      this.dedicatedQuestionTypes = list.RequestFormQuestionType.filter(x => x.dedicated_screen).map(x => x.id);
    });
    // Pre-request forms are staff-only. An existing request already has its
    // screens; calling the list as an approver only returns 403.
    if (!this.model.id) {
      this.preRequestFormService.list({
        company_id: this.getCurrentCompanyId()
      }, {
        bureau_id: this.getCurrentBureauId()
      }).subscribe((res) => {
        if (res && res.length) {
          this.preRequestForm = res[0];
        }
      });
    }
    this.dtOptionsClients = DatatablesUtils.buildDatatablesSettings({
      columns: [
        { data: null, searchable: false, orderable: false },
        { data: 'last_name' },
        { data: 'first_name' },
        { data: null, orderable: false },
        { data: 'title' },
        { data: 'email' },
        { data: 'office_phone' },
      ],
      order: [1, 'asc'],
      processing: true,
      serverSide: true,
      searching: false,
      ajax: (dataTablesParameters: any, callback) => {
        this.clientService.listPage({
          company_id: this.getCurrentCompanyId()
        }, {
          bureau_id: this.getCurrentBureauId(),
          area_required: true,
          can_request: true,
        }, dataTablesParameters)
          .subscribe(res => {
            this.clients = res.clients;
            if (this.model.client_id) {
              this.client = this.clients.find(c => c.id === this.model.client_id);
            }
            callback({
              draw: dataTablesParameters.draw,
              recordsTotal: res.total,
              recordsFiltered: res.total,
              data: []
            });
          });
      },
    });
    this.dtOptionsSpeakers = DatatablesUtils.buildDatatablesSettings({
      columns: [
        { data: null, searchable: false, orderable: false },
        { data: 'last_name' },
        { data: 'first_name' },
        { data: 'degrees', orderable: false },
        { data: 'city', orderable: false },
        { data: 'state', orderable: false },
        { data: 'affiliation', orderable: false },
      ],
      order: [1, 'asc'],
      processing: true,
      serverSide: true,
      paging: false,
      searching: false,
      info: false,
      ajax: (dataTablesParameters: any, callback) => {
        this.model.setEventDateTimeUTC(this.meetingType.duration_hours);
        this.requestService.getSpeakers({
          company_id: this.getCurrentCompanyId(),
        }, {
          bureau_id: this.getCurrentBureauId(),
          meeting_type_id: this.meetingType.id,
          requested_topic_id: this.model.topic_id,
          event_start_at: this.model.request_datetimes[0] && this.model.request_datetimes[0].event_start_at ? this.model.request_datetimes[0].event_start_at.toISOString() : null,
          event_end_at: this.model.request_datetimes[0] && this.model.request_datetimes[0].event_end_at ? this.model.request_datetimes[0].event_end_at.toISOString() : null,
          requestable: true,
        }, dataTablesParameters)
          .subscribe(res => {
            this.speakers = res.speakers;
            callback({
              draw: dataTablesParameters.draw,
              recordsTotal: res.total,
              recordsFiltered: res.total,
              data: []
            });
          });
      },
    });
    this.dtOptionsVenues = DatatablesUtils.buildDatatablesSettings({
      columns: [
        { data: null, searchable: false, orderable: false },
        { data: 'name' },
        { data: 'city' },
        { data: 'state' },
        { data: 'zip' },
      ],
      order: [1, 'asc'],
      processing: true,
      serverSide: true,
      searching: true,
      initComplete: (settings: any) => {
        try {
          const api = new $.fn.dataTable.Api(settings);
          const filter = $(api.table().container()).find('.dataTables_filter');
          if (!filter.length || filter.find('.js-venue-state-filter').length) {
            return;
          }
          const label = $('<label class="me-1 mb-0 align-middle text-nowrap">State</label>');
          const select = $('<select class="form-control form-control-sm d-inline-block w-auto me-2 js-venue-state-filter"></select>');
          select.append($('<option></option>').val('').text(''));
          US_STATES.forEach((st) => {
            select.append($('<option></option>').val(st).text(st));
          });
          select.val(this.venueFilterState || '');
          select.on('change', () => {
            this.venueFilterState = String(select.val() || '');
            api.ajax.reload(undefined, false);
          });
          filter.prepend(select);
          filter.prepend(label);
        } catch (e) {
          console.warn('Venue state filter init failed', e);
        }
      },
      ajax: (dataTablesParameters: any, callback) => {
        this.vendorService.listPage({
          company_id: this.getCurrentCompanyId(),
        }, {
          type: 'venue',
          meeting_type_id: this.model.meeting_type_id,
          requestable: true,
          ...(this.venueFilterState ? { state: this.venueFilterState } : {}),
        }, dataTablesParameters).subscribe(res => {
          this.venues = res.vendors;
          callback({
            draw: dataTablesParameters.draw,
            recordsTotal: res.total,
            recordsFiltered: res.total,
            data: []
          });
        });
      },
    });
    this.dtOptionsCaterers = DatatablesUtils.buildDatatablesSettings({
      columns: [
        { data: null, searchable: false, orderable: false },
        { data: 'name' },
        { data: 'city' },
        { data: 'state' },
        { data: 'zip' },
      ],
      order: [1, 'asc'],
      processing: true,
      serverSide: true,
      searching: true,
      ajax: (dataTablesParameters: any, callback) => {
        this.vendorService.listPage({
          company_id: this.getCurrentCompanyId(),
        }, {
          type: 'venue,caterer',
          meeting_type_id: this.model.meeting_type_id,
          requestable: true,
        }, dataTablesParameters).subscribe(res => {
          this.caterers = res.vendors;
          if (this.model.caterer_id) {
            this.caterer = this.caterers.find(c => c.id === this.model.caterer_id);
          }
          callback({
            draw: dataTablesParameters.draw,
            recordsTotal: res.total,
            recordsFiltered: res.total,
            data: []
          });
        });
      },
    });
    if (this.model.client) {
      this.client = this.model.client;
    }
    if (this.model.request_speakers) {
      this.selectedSpeakers = this.model.request_speakers.map(rs => rs.speaker);
    }
    if (this.model.request_venues) {
      this.selectedVenues = this.model.request_venues.map(rv => rv.venue);
    }
    if (this.model.request_datetimes) {
      this.selectedEventDates = this.model.request_datetimes.map((rd) => {
        const datetime = (rd.event_date && rd.event_time) ? new Date(`${rd.event_date} ${rd.event_time}`) : null;
        this.event_time = datetime;
        return {
          event_date: datetime
        }
      });
    }
    if (this.model.av_equipments) {
      this.selectedAvEquipments = this.model.av_equipments;
      this.model.av_equipments = this.model.av_equipments.map(e => e.av_equipment_id);
    }
    if (this.model.meeting_type) {
      this.meetingType = this.model.meeting_type;
    }
    if (this.model.topic) {
      this.topic = this.model.topic;
    }
    // Go to First page
    if (this.model.id && !this.previewModalFlag) {
      this.nextStep('client');
      this.toLastStep = true;
      this.clientSelected(this.model.client);
    } else {
      this.nextStep('client');
    }
  }

  get currentStep() {
    return this.steps[this.steps.length - 1];
  }

  isCurrentStepQuestions() {
    return (typeof this.currentStep === 'number');
  }

  isStepQuestionsNotNextable() {
    if (this.previewModalFlag) {
      return false;
    }

    const attendeeScreen = this.model.screens[this.currentStep].questions.find(q => q.type_id === 'attendees');
    if (attendeeScreen) {
      return this.model.attendees && this.model.attendees.length < (attendeeScreen.min || 1);
    }
    const speakerScreen = this.model.screens[this.currentStep].questions.find(q => q.type_id === 'speakers');
    if (speakerScreen) {
      return this.selectedSpeakers && this.selectedSpeakers.length < (speakerScreen.min || 1);
    }
    const venueScreen = this.model.screens[this.currentStep].questions.find(q => q.type_id === 'venue');
    if (venueScreen) {
      return this.selectedVenues && this.selectedVenues.length < (venueScreen.min || 1);
    }
    const datetimeScreen = this.model.screens[this.currentStep].questions.find(q => q.type_id === 'event_start_at');
    if (datetimeScreen) {
      return this.model.request_datetimes && this.model.request_datetimes.filter(dt => dt.event_date).length < (datetimeScreen.min || 1) || this.hasDuplicatedDates();
    }
    for (let i = 0; i < this.model.screens[this.currentStep].questions.length; i++) {
      const question = this.model.screens[this.currentStep].questions[i];
      if (this.dedicatedQuestionTypes.includes(question.type_id)) {
        continue;
      }
      // Skip display-only question types (PARAGRAPH, HEADER) - they don't require answers
      if (question.type_id === 'PARAGRAPH' || question.type_id === 'HEADER') {
        continue;
      }
      // Only check required validation if question is not optional
      if (!question.optional) {
        // Ensure answers object is initialized
        if (!this.model.answers[this.currentStep]) {
          this.model.answers[this.currentStep] = {};
        }
        const answer = this.model.answers[this.currentStep][i];
        
        // For MULTIPLE_CHECK, check if at least one option is selected
        if (question.type_id === 'MULTIPLE_CHECK') {
          if (!answer || typeof answer !== 'object' || Object.keys(answer).length === 0) {
            return true;
          }
          
          if (Array.isArray(answer)) {
            if (answer.length === 0) return true;
          } else {
            // Check if all values are false
            const hasSelected = Object.values(answer).some(val => val === true);
            if (!hasSelected) {
              return true;
            }
          }
          // Skip the general empty check for MULTIPLE_CHECK since we already validated it above
          continue;
        }
        
        // For MULTIPLE_RADIO, check if answer is selected
        // If "__OTHER__" is selected, also check if other_text is provided
        if (question.type_id === 'MULTIPLE_RADIO') {
          if (answer === null || answer === undefined || answer === '' || (typeof answer === 'string' && answer.trim() === '')) {
            return true;
          }
          // If "Other" is selected, check if other text is provided
          if (answer === '__OTHER__') {
            const otherText = this.model.answers[this.currentStep] ? this.model.answers[this.currentStep][i + '_other_text'] : null;
            if (!otherText || (typeof otherText === 'string' && otherText.trim() === '')) {
              return true;
            }
          }
          // Skip the general empty check for MULTIPLE_RADIO since we already validated it above
          continue;
        }
        
        // For FILE type, check if it's an object with data
        if (question.type_id === 'FILE') {
          if (!answer || typeof answer !== 'object' || !answer.data) {
            return true;
          }
          // Skip the general empty check for FILE since we already validated it above
          continue;
        }
        
        // For YN (Yes/No) type: Yes, No, or when na is set, '' (N/A)
        if (question.type_id === 'YN') {
          if (question.na && answer === '') {
            continue;
          }
          if (answer === null || answer === undefined || answer === '' || (answer !== 'Yes' && answer !== 'No')) {
            return true;
          }
          continue;
        }
        
        // For TF (True/False) type, check if answer is "True" or "False"
        if (question.type_id === 'TF') {
          if (answer === null || answer === undefined || answer === '' || (answer !== 'True' && answer !== 'False')) {
            return true;
          }
          // Skip the general empty check for TF since we already validated it above
          continue;
        }
        
        // Handle different answer types - general check for other types
        if (answer === null || answer === undefined || answer === '') {
          return true;
        }
        // For string answers, check if empty after trimming
        if (typeof answer === 'string' && answer.trim() === '') {
          return true;
        }
      }
    }
    return false;
  }

  getRequiredDatesNum() {
    return this.model.screens[this.currentStep].questions.find(q => q.type_id === 'event_start_at').min || 1;
  }

  findSkipQuestion(questions, answers, prefix) {
    let skip = false;
    let question = null;
    for (let i = 0; i < questions.length; i++) {
      const quest = questions[i];
      const answer = answers[prefix + i];
      if (quest.subQuestions) {
        const result = this.findSkipQuestion(quest.subQuestions, answers, i + '_sub_');
        skip = result.skip;
        question = result.question;
      }
      if ((quest.sub_type == 'IFYESSKIP' && answer == 'Yes') || (quest.sub_type == 'IFNOSKIP' && answer == 'No')) {
        skip = true;
        question = quest;
        break;
      }
    }
    return { skip, question };
  }

  validateAttendeeEstimate(screenIndex?: number, questionIndex?: number): boolean {
    // Validate all screens or a specific screen
    const screensToCheck = screenIndex !== undefined ? [screenIndex] : this.model.screens.map((_, index) => index);
    
    for (const step of screensToCheck) {
      if (this.model.screens[step]) {
        const questions = this.model.screens[step].questions;
        // If questionIndex is provided, only check that specific question
        const questionsToCheck = questionIndex !== undefined ? [questionIndex] : questions.map((_, index) => index);
        
        for (const qIndex of questionsToCheck) {
          const question = questions[qIndex];
          if (question.type_id === 'attendee_count_estimate') {
            // Check both answer formats: [qIndex] and [0] (for single-question screens)
            let answer = this.model.answers[step] ? this.model.answers[step][qIndex] : undefined;
            
            if (answer !== undefined && answer !== null && answer !== '') {
              const answerValue = parseFloat(answer);
              if (!isNaN(answerValue)) {
                let minRequired;
                
                // Check if question has min_attendee field, otherwise use meeting type min_hcp
                if (question.min_attendee) {
                  minRequired = question.min_attendee;
                } else if (this.meetingType) {
                  minRequired = this.model.priority === 'secondary_event' ? this.meetingType.secondary_min_hcp : this.meetingType.min_hcp;
                } else {
                  continue; // Skip if no meeting type yet
                }
                
                if (minRequired && answerValue < minRequired) {
                  this.toastr.error(`Attendee Estimate must be at least ${minRequired}. You entered ${answerValue}.`);
                  return false;
                }
              }
            }
          }
          if (question.type_id === 'NUMBER' && question.min != null && question.min !== '') {
            let answer = this.model.answers[step] ? this.model.answers[step][qIndex] : undefined;
            if (answer !== undefined && answer !== null && answer !== '') {
              const answerValue = parseFloat(answer);
              if (!isNaN(answerValue)) {
                const minRequired = parseFloat(question.min);
                if (!isNaN(minRequired) && answerValue < minRequired) {
                  this.toastr.error(`Value must be at least ${minRequired}. You entered ${answerValue}.`);
                  return false;
                }
              }
            }
          }
        }
      }
    }
    return true;
  }

  /** Status label used for "Hide IF Status" (e.g. START, RM Pending, TRIO Review). */
  getCurrentRequestStatusLabel(): string {
    if (!this.model.id) {
      return 'START';
    }
    if (this.model.status === 'pending' && (this.model.pending_status_label || this.model.request_status?.status_label)) {
      return this.model.pending_status_label || this.model.request_status?.status_label;
    }
    return this.model.getStatusLabel?.() || this.model.status || '';
  }

  /** True if the screen at screenIndex should be hidden for the current request status. */
  isScreenHidden(screenIndex: number): boolean {
    const screen = this.model.screens?.[screenIndex];
    if (!screen || !Array.isArray(screen.hide_if_statuses) || screen.hide_if_statuses.length === 0) {
      return false;
    }
    const current = this.getCurrentRequestStatusLabel();
    return screen.hide_if_statuses.some((s: string) => s === current);
  }

  /** Returns the next visible screen index, or 'END' if none. */
  private nextVisibleScreenAfter(fromIndex: number): number | 'END' {
    let idx = fromIndex;
    while (idx < this.model.screens.length && this.isScreenHidden(idx)) {
      idx += 1;
    }
    return idx >= this.model.screens.length ? 'END' : idx;
  }

  /** Screens that are visible for the current request status (for edit/preview list). */
  getVisibleScreensForPreview(): { index: number; screen: any }[] {
    if (!this.model.screens || !this.model.screens.length) {
      return [];
    }
    return this.model.screens
      .map((screen, index) => ({ index, screen }))
      .filter(({ index, screen }) => !this.isScreenHidden(index) && this.hasNonDedicatedQuestions(screen));
  }

  nextNumberStep(step) {
    // check attendee estimation from meeting type or question min_attendee
    if (!this.validateAttendeeEstimate(step)) {
      return;
    }
    
    let nextScreen = null;
    let skip = false;
    let question;
    if (this.model.screens[step] != undefined && this.model.answers[step] != undefined) {
      const result = this.findSkipQuestion(this.model.screens[step].questions, this.model.answers[step], '');
      skip = result.skip;
      question = result.question;
    }
    if (skip) {
      nextScreen = question.skipScreen == 'END' ? question.skipScreen : parseInt(question.skipScreen);
      this.clearAnswers(step, nextScreen);
    } else if (step === -1) {
      if (this.model.screens.length) {
        nextScreen = this.nextVisibleScreenAfter(0);
      } else {
        nextScreen = 'END';
      }
    } else {
      const branches = this.model.screens[step].branches;
      const nextSequentialScreen = step + 1 < this.model.screens.length ? step + 1 : 'END';
      if (!branches || branches.length === 0) {
        // No branches: use sequential flow so all screens are shown
        nextScreen = nextSequentialScreen;
      } else {
        let defaultBranch = null;
        let foundBranch = null;
        branches.forEach((branch) => {
          if (!foundBranch) {
            if (branch.answers) {
              let match = true;
              Object.keys(branch.answers).forEach((q) => {
                if (!this.model.answers[step] || this.model.answers[step][q] !== branch.answers[q]) {
                  match = false;
                }
              });
              if (match) {
                foundBranch = branch;
              }
            } else {
              defaultBranch = branch;
            }
          }
        });
        if (!foundBranch) {
          foundBranch = defaultBranch;
        }
        nextScreen = foundBranch ? foundBranch.next_screen : nextSequentialScreen;
      }
    }
    // Skip screens that are hidden for the current request status (Hide IF Status)
    if (typeof nextScreen === 'number') {
      nextScreen = this.nextVisibleScreenAfter(nextScreen);
    }
    if (nextScreen === 'END' || (typeof nextScreen === 'number' && nextScreen >= this.model.screens.length)) {
      this.nextStep('preview');
    } else {
      this.nextStep(nextScreen);
    }
  }

  nextStep(step) {
    if (typeof step === 'number') {
      this.loadScreenQuestions(step, (s, questionType) => {
        this.steps.push(step);
        if (this.toLastStep) {
          this.nextClicked();
        }
      });
    } else {
      this.steps.push(step);
      if (this.toLastStep) {
        this.nextClicked();
      }
    }
  }

  prevStep() {
    this.steps.pop();
    if (!this.model.id && this.currentStep === 'meeting_type' && this.preRequestForm && this.preRequestForm.screens && this.preRequestForm.screens.length) {
      this.steps.pop();
    }
  }

  firstStep() {
    while (this.steps.length > 1) {
      this.steps.pop();
    }
  }

  nextClicked() {
    if (this.currentStep === 'client') {
      this.client && this.clientSelected(this.client);
    } else if (this.currentStep === 'area') {
      this.selectedArea && this.areaSelected(this.selectedArea);
    } else if (this.currentStep === 'meeting_type') {
      this.meetingType && this.meetingTypeSelected(this.meetingType);
    } else if (this.currentStep === 'priority') {
      this.model.priority && this.prioritySelected(this.model.priority);
    } else if (this.isCurrentStepQuestions() && (this.toLastStep || !this.isStepQuestionsNotNextable())) {
      this.nextNumberStep(this.currentStep);
    } else if (this.isCurrentPreRequestQuestions()) {
      this.nextPreRequestStep();
    } else {
      this.toLastStep = false;
    }
  }

  closeClicked() {
    if (this.previewEvent) {
      this.previewEvent.emit();
    }
  }

  lastStep() {
    this.toLastStep = true;
    this.nextClicked();
  }

  getScreenIndex(typeId) {
    for (let i = 0; i < this.model.screens.length; i++) {
      if (this.model.screens[i].questions.find(q => q.type_id === typeId)) {
        return i;
      }
    }
    return null;
  }

  isDedicatedScreenIncomplete(typeId: string): boolean {
    const screenIndex = this.getScreenIndex(typeId);
    if (screenIndex === null || this.isScreenHidden(screenIndex)) return false;
    const question = this.model.screens[screenIndex].questions.find(q => q.type_id === typeId);
    if (!question) return false;
    const min = question.min || 1;
    if (typeId === 'speakers') {
      return !this.selectedSpeakers || this.selectedSpeakers.length < min;
    }
    if (typeId === 'event_start_at') {
      return !this.model.request_datetimes || this.model.request_datetimes.filter(dt => dt.event_date).length < min;
    }
    if (typeId === 'venue') {
      return !this.selectedVenues || this.selectedVenues.length < min;
    }
    if (typeId === 'attendees') {
      return !this.model.attendees || this.model.attendees.length < min;
    }
    if (typeId === 'topic') {
      return !this.topic;
    }
    return false;
  }

  isCustomQuestionIncomplete(screenIndex: number, questionIndex: number): boolean {
    if (!this.model.screens || !this.model.answers) return false;
    const screen = this.model.screens[screenIndex];
    if (!screen) return false;
    const dedicated = this.dedicatedQuestionTypes || [];
    const q = (screen.questions || [])[questionIndex];
    if (!q || dedicated.indexOf(q.type_id) !== -1 || q.type_id === 'PARAGRAPH' || q.type_id === 'HEADER' || q.optional) return false;
    const ans = this.model.answers[screenIndex];
    const answer = ans ? ans[questionIndex] : undefined;
    if (q.type_id === 'MULTIPLE_CHECK') {
      if (!answer || typeof answer !== 'object' || Object.keys(answer).length === 0) return true;
      if (Array.isArray(answer)) {
        if (answer.length === 0) return true;
      } else {
        if (!Object.values(answer).some((v) => v === true)) return true;
      }
      return false;
    }
    if (q.type_id === 'MULTIPLE_RADIO') {
      if (answer === null || answer === undefined || answer === '' || (typeof answer === 'string' && answer.trim() === '')) return true;
      if (answer === '__OTHER__') {
        const other = ans ? ans[questionIndex + '_other_text'] : null;
        if (!other || (typeof other === 'string' && other.trim() === '')) return true;
      }
      return false;
    }
    if (q.type_id === 'FILE') {
      return !answer || typeof answer !== 'object' || !answer.data;
    }
    if (q.type_id === 'YN') {
      if (q.na && answer === '') {
        return false;
      }
      return answer === null || answer === undefined || answer === '' || (answer !== 'Yes' && answer !== 'No');
    }
    if (q.type_id === 'TF') {
      return answer === null || answer === undefined || answer === '' || (answer !== 'True' && answer !== 'False');
    }
    if (answer === null || answer === undefined || answer === '') return true;
    if (typeof answer === 'string' && answer.trim() === '') return true;
    return false;
  }

  isCustomScreenIncomplete(screenIndex: number): boolean {
    if (!this.model.screens) return false;
    if (this.isScreenHidden(screenIndex)) return false;
    const screen = this.model.screens[screenIndex];
    if (!screen) return false;
    const questions = screen.questions || [];
    for (let qi = 0; qi < questions.length; qi++) {
      if (this.isCustomQuestionIncomplete(screenIndex, qi)) return true;
    }
    return false;
  }

  moveTo(step) {
    if (this.model.id) {
      while (this.steps.length > 1) {
        this.steps.pop();
      }
      this.nextStep(step);
    } else {
      while (this.steps.length > 1 && this.currentStep !== step) {
        this.steps.pop();
      }
      if (this.currentStep !== step) {
        this.nextClicked();
        if (this.currentStep !== step) {
          this.nextStep(step);
        }
      }
    }
  }

  clientSelected(client) {
    this.client = client;
    this.model.client_id = client.id;
    this.model.timezone_id = client.timezone_id;
    this.meetingTypes = [];
    this.meetingType = null;
    if (!this.model.id) {
      this.model.meeting_type_id = null;
    }

    const bureauId = this.getCurrentBureauId();
    const areas = (client.areas || []).filter((a) => !a.bureau_id || a.bureau_id === bureauId);
    this.clientAreas = areas;

    if (!areas.length) {
      // Edit path often has client without areas hydrated; keep existing area_id
      if (this.model.area_id) {
        this.selectedArea = this.model.area || { area_id: this.model.area_id, name: this.model.area?.name, description: this.model.area?.description };
        this.loadMeetingTypesForClient();
        return;
      }
      this.model.area_id = null;
      this.selectedArea = null;
      this.toastr.error('Host has no area aligned for this bureau.');
      return;
    }

    if (areas.length === 1) {
      this.areaSelected(areas[0], { skipStep: true });
      return;
    }

    if (this.model.area_id) {
      this.selectedArea = areas.find((a) => a.area_id === this.model.area_id || a.area?.id === this.model.area_id)
        || this.model.area
        || null;
    } else {
      this.selectedArea = null;
      this.model.area_id = null;
    }

    this.nextStep('area');
    // Multi-area: do not load meeting types until Area is chosen (except edit with area already set)
    if (this.selectedArea && this.model.id) {
      this.loadMeetingTypesForClient();
    }
  }

  areaSelected(area, options: { skipStep?: boolean } = {}) {
    this.selectedArea = area;
    this.model.area_id = area.area_id || area.area?.id;
    if (!options.skipStep && this.currentStep !== 'area') {
      this.nextStep('area');
    }
    this.loadMeetingTypesForClient();
  }

  loadMeetingTypesForClient() {
    if (!this.client || !this.model.area_id) {
      return;
    }
    this.meetingTypeService.list({
      company_id: this.getCurrentCompanyId(),
    }, {
      bureau_id: this.getCurrentBureauId(),
      client_id: this.client.id,
      requestable: true,
    })
      .subscribe(result => {
        this.meetingTypes = result || [];
        if (this.model.meeting_type_id) {
          this.meetingType = this.meetingTypes.find(m => m.id === this.model.meeting_type_id);
          // Approvers are filtered to their own level, so the host's saved
          // event type (e.g. Virtual) may be missing. Keep it so Edit can
          // walk on to the screens that still need answers.
          if (!this.meetingType && this.model.meeting_type) {
            this.meetingType = this.model.meeting_type;
            this.meetingTypes = [this.model.meeting_type, ...this.meetingTypes];
          }
        }
        this.nextStep('meeting_type');
        if (!this.model.id && this.meetingTypes.length) {
          if (this.meetingTypes.length === 1) {
            this.meetingTypeSelected(this.meetingTypes[0]);
          } else if (this.preRequestForm && this.preRequestForm.screens && this.preRequestForm.screens.length) {
            this.model.pre_request_form_id = this.preRequestForm.id;
            this.model.pre_request_screens = this.preRequestForm.screens;
            this.nextStep('pre_request_form/0');
          }
        }
      });
  }

  isCurrentPreRequestQuestions() {
    return this.currentStep && this.currentStep.toString().includes('pre_request');
  }

  currentPreRequestStep() {
    return parseInt(this.currentStep.split('/')[1], 10);
  }

  nextPreRequestStep() {
    const i = this.currentPreRequestStep();
    const screen = this.model.pre_request_screens[i];
    const branches = screen.branches || [];
    let meetingTypeId = null;
    branches.forEach((branch) => {
      if (!meetingTypeId) {
        if (branch.answers) {
          let match = true;
          Object.keys(branch.answers).forEach((q) => {
            if (!this.model.pre_request_answers[i] || this.model.pre_request_answers[i][q] !== branch.answers[q]) {
              match = false;
            }
          });
          if (match) {
            meetingTypeId = branch.meeting_type_id;
          }
        }
      }
    });
    if (!meetingTypeId && i + 1 === this.model.pre_request_screens.length) {
      meetingTypeId = this.preRequestForm.default_meeting_type_id;
    }
    if (meetingTypeId) {
      this.meetingTypeSelected(this.meetingTypes.find(m => m.id.toString() === meetingTypeId.toString()));
    } else {
      this.nextStep('pre_request_form/' + (i + 1));
    }
  }

  meetingTypeSelected(meetingType) {
    this.model.meeting_type_id = meetingType.id;
    this.meetingType = meetingType;
    this.requestFormService.search({
      company_id: this.getCurrentCompanyId()
    }, {
      bureau_id: this.getCurrentBureauId(),
      meeting_type_id: this.model.meeting_type_id,
    }).subscribe(request_forms => {
      if (request_forms.length > 0) {
        this.requestForms = request_forms;
        const formPriorities = this.requestForms.map(f => f.priority);
        this.priorities = REQUEST_PRIORITIES.filter(p => formPriorities.includes(p.id));
      } else {
        this.toastr.error('Failed to find proper request forms!');
        return;
      }
      if (request_forms.length === 1) {
        this.prioritySelected(request_forms[0].priority);
        this.priorityAutomaticallySelected = true;
      } else {
        this.nextStep('priority');
        this.priorityAutomaticallySelected = false;
      }
    });
  }

  prioritySelected(priority) {
    this.model.priority = priority;
    const request_form = this.requestForms.find(f => f.priority?.toLowerCase() === priority?.toLowerCase());
    if (request_form) {
      this.model.request_form_id = request_form.id;
      this.model.screens = request_form.screens || [];
      this.model.screens.forEach((screen) => {
        screen.questions.sort((a, b) => (a.rank || 0) - (b.rank || 0));
      });
    } else {
      this.toastr.error('Failed to find a proper request form!');
      return;
    }
    if (this.model.id && this.toLastStep) {
      this.toLastStep = false;
      this.nextStep('preview');
    } else {
      this.nextNumberStep(-1);
    }
  }

  loadScreenQuestions(step, cb) {
    const questions = this.model.screens[step].questions;
    const questionType = questions[0].type_id;
    if (questions[0].type_id === 'av_equipments') {
      this.equipmentService.list({
        company_id: this.getCurrentCompanyId(),
        bureau_id: this.getCurrentBureauId(),
      }).subscribe(res => {
        this.avEquipments = res.av_equipments;
        cb(step, questionType);
      });
    } else if (questions[0].type_id === 'topic') {
      this.topicService.list(
        { company_id: this.getCurrentCompanyId() },
        {
          bureau_id: this.getCurrentBureauId(),
          ...this.model.request_speakers && { speaker_id: this.model.request_speakers.map(rs => rs.speaker_id) },
          meeting_type_id: this.model.meeting_type_id,
          requestable: true,
        }
      ).subscribe(topics => {
        this.topics = topics;
        if (this.model.topic_id) {
          this.topic = this.topics.find(t => t.id === this.model.topic_id);
        }
        cb(step, questionType);
      });
    } else if (questions[0].type_id === 'event_start_at') {
      for (let i = this.model.request_datetimes.length; i < questions[0].max; i++) {
        this.model.request_datetimes.push({});
        this.selectedEventDates.push({
          event_date: null
        });
      }
      // Initialize event_time with a default so the timepicker's internal store
      // always has a valid Date. Without this, typing an hour and leaving minutes
      // blank means the hour never commits to the store, and toggling AM/PM
      // resets the displayed hour (the store defaults to midnight + 12 = 12:00 PM).
      if (!this.event_time) {
        const defaultTime = new Date();
        defaultTime.setHours(12, 0, 0, 0);
        this.event_time = defaultTime;
      }
      // Initialize end_time from duration_hours
      if (!this.end_time && this.event_time && this.meetingType) {
        const startMoment = moment(this.event_time);
        this.end_time = startMoment.clone().add(this.meetingType.duration_hours, 'hours').toDate();
      }
      this.datePickerDisabled = true;
      this.requestService.getTimeSlots({
        company_id: this.getCurrentCompanyId()
      }, {
        bureau_id: this.getCurrentBureauId(),
        meeting_type_id: this.meetingType.id,
        speaker_id: this.model.request_speakers.map(rs => rs.speaker_id).join(','),
      }).subscribe(res => {
        this.datePickerDisabled = res.no_available_dates;
        this.minDate = res.min_date ? moment(res.min_date).toDate() : new Date();
        this.model.request_datetimes.forEach((rd) => {
          if (rd.event_start_at && rd.event_start_at < this.minDate!) {
            this.minDate = null;
          }
        });
        this.maxDate = res.max_date ? moment(res.max_date).toDate() : null;
        // TODO: disable time slots for res.events
        cb(step, questionType);
      });
    } else {
      cb(step, questionType);
    }
  }

  hasDuplicatedDates() {
    for (let i = 0; i < this.model.request_datetimes.length - 1; i++) {
      for (let j = i + 1; j < this.model.request_datetimes.length; j++) {
        if (this.model.request_datetimes[i].event_date && this.model.request_datetimes[i].event_date === this.model.request_datetimes[j].event_date) {
          return true;
        }
      }
    }
    return false;
  }

  dateSelected(index, date: Date) {
    if (date) {
      this.selectedEventDates[index].event_date = date;

      this.model.request_datetimes[index].event_date = moment(date).format('YYYY-MM-DD');

      let sameDateSelected = false;
      this.model.request_datetimes.forEach((rd, i) => {
        if (i !== index && rd.event_date === this.model.request_datetimes[index].event_date) {
          sameDateSelected = true;
        }
      });
      if (sameDateSelected) {
        this.toastr.error('You can not select the same date more than once.');
      }
    } else {
      this.selectedEventDates[index].event_date = null;
      this.model.request_datetimes[index].event_date = null;
    }
  }

  timeSelected() {
    this.model.request_datetimes.forEach((rd) => {
      rd.event_time = moment(this.event_time).format('HH:mm');
    });
    // Only set default end time when not already set
    if (this.event_time && !this.end_time && this.meetingType) {
      const startMoment = moment(this.event_time);
      this.end_time = startMoment.clone().add(this.meetingType.duration_hours, 'hours').toDate();
      this.endTimeSelected();
    }
  }

  endTimeSelected() {
    const endTimeStr = moment(this.end_time).format('HH:mm');
    this.model.request_datetimes.forEach((rd) => {
      rd.end_time = endTimeStr;
    });
  }

  topicSelected(topic) {
    this.model.topic_id = topic.id;
    this.topic = topic;
  }

  isSpeakerSelected(speaker) {
    return this.model.request_speakers.map(rs => rs.speaker_id).includes(speaker.id);
  }

  /** User-friendly reason why a speaker is not selectable (for tooltip). */
  getSpeakerRequestableReason(speaker: { requestable?: boolean; requestable_reason?: string }): string {
    if (speaker.requestable) {
      return '';
    }
    const reason = speaker.requestable_reason;
    const messages: Record<string, string> = {
      status_id: 'Speaker status does not allow requests for this bureau.',
      contract_status_id: 'Contract status does not allow requests for this bureau.',
      occupied: 'Speaker is already booked for the selected date(s).',
      untrained_topic: 'Speaker is not approved for the selected topic.',
      untrained_event_training: 'Speaker has not completed required event training.',
    };
    return (reason && messages[reason]) || (reason || 'Not available for request.');
  }

  /** True when the list has speakers but none are selectable. */
  get hasNoRequestableSpeakers(): boolean {
    return !!(
      this.speakers &&
      this.speakers.length > 0 &&
      !this.speakers.some((s: any) => s.requestable === true)
    );
  }

  toggleSpeaker(speaker) {
    const speakerScreen = this.model.screens[this.currentStep].questions.find(q => q.type_id === 'speakers');
    if (this.isSpeakerSelected(speaker)) {
      const i = this.model.request_speakers.map(rs => rs.speaker_id).indexOf(speaker.id);
      this.model.request_speakers.splice(i, 1);
      this.selectedSpeakers.splice(i, 1);
    } else if (speakerScreen.max == null || this.selectedSpeakers.length < speakerScreen.max) {
      this.model.request_speakers.push({
        speaker_id: speaker.id,
      });
      this.selectedSpeakers.push(speaker);
    }
  }

  isVenueSelected(venue) {
    return this.model.request_venues.map(rv => rv.venue_id).includes(venue.id);
  }

  toggleVenue(venue) {
    const venueScreen = this.model.screens[this.currentStep].questions.find(q => q.type_id === 'venue');
    if (this.isVenueSelected(venue)) {
      const i = this.model.request_venues.map(rv => rv.venue_id).indexOf(venue.id);
      this.model.request_venues.splice(i, 1);
      this.selectedVenues.splice(i, 1);
    } else if (venueScreen.max == null || this.selectedVenues.length < venueScreen.max) {
      this.model.request_venues.push({
        venue_id: venue.id,
      });
      this.selectedVenues.push(venue);
    }
  }

  catererSelected(caterer) {
    if (caterer === 'venue') {
      this.model.caterering_by_venue = true;
      this.model.caterer_id = null
      this.caterer = null;
    } else {
      this.model.caterering_by_venue = false;
      this.model.caterer_id = caterer.id;
      this.caterer = caterer;
    }
  }

  onChangeAvEquipments() {
    this.selectedAvEquipments = this.model.av_equipments.map(id => this.avEquipments.find(av => av.id === id));
  }

  /** "None" indicator: explicit empty selection. Clears any selected A/V equipment. */
  selectNoAvEquipments(event: Event) {
    event.preventDefault();
    this.model.av_equipments = [];
    this.selectedAvEquipments = [];
  }

  newAudience() {
    this.bsAttendeeSearchModalRef = this.modalService.show(CallPlanSearchModalComponent, {
      initialState: {
        enableCreate: true,
        multiSelectable: true,
        includeCallPlans: true,
        includeAttendees: true,
        states: this.states,
        companyId: this.getCurrentCompanyId(),
        divisionId: this.getBureau()?.division_id,
        bureauId: this.model.bureau_id,
      },
      class: 'modal-lg',
    });
    this.bsAttendeeSearchModalRef.content.event.subscribe(value => {
      if (value == 'new_attendee') {
        this.bsAttendeeNewModalRef = this.modalService.show(CallPlanNewModalComponent, {
          initialState: {
            states: this.states,
            professions: this.professions,
            degrees: this.degrees,
            specialties: this.specialties,
          },
          class: 'modal-lg'
        });
        this.bsAttendeeNewModalRef.content.event.subscribe(value => {
          this.bsAttendeeNewModalRef.hide();
          this.model.attendees.push(value);
        });
        this.bsAttendeeSearchModalRef.hide();
      } else {
        Array.prototype.push.apply(this.model.attendees, value);
      }
    });
  }

  onRemoveAudience(index) {
    this.model.attendees.splice(index, 1);
  }

  validateConfirmFields(): boolean {
    if (this.model.screens && this.model.answers) {
      for (let screenIndex = 0; screenIndex < this.model.screens.length; screenIndex++) {
        const screen = this.model.screens[screenIndex];
        if (!screen || !screen.questions || !this.model.answers[screenIndex]) continue;
        for (let questionIndex = 0; questionIndex < screen.questions.length; questionIndex++) {
          const question = screen.questions[questionIndex];
          if (!question.confirm) continue;
          const answer = this.model.answers[screenIndex][questionIndex];
          const confirmAnswer = this.model.answers[screenIndex][questionIndex + '_confirm'];
          if (answer && !confirmAnswer) return false;
          if (!answer && confirmAnswer) return false;
          if (answer && confirmAnswer && answer !== confirmAnswer) return false;
        }
      }
    }
    return true;
  }

  updateRequest() {
    // Validate confirm field matches
    if (!this.validateConfirmFields()) {
      this.toastr.error('Confirm fields do not match. Please correct before submitting.');
      return;
    }

    // Validate attendee estimates before submitting
    if (!this.validateAttendeeEstimate()) {
      return;
    }

    this.model.setEventDateTimeUTC(this.meetingType.duration_hours);
    this.requestService.update(this.model, {
      company_id: this.getCurrentCompanyId()
    })
      .subscribe(response => {
        this.toastr.success('Updated!');
        this.navigate(this.router, this.returnUrl, this.returnUrlParams);
      }, err => {
        this.toastr.error(getErrorMessage(err, 'Failed to update!'));
      });
  }

  createRequest() {
    // Validate confirm field matches
    if (!this.validateConfirmFields()) {
      this.toastr.error('Confirm fields do not match. Please correct before submitting.');
      return;
    }

    // Validate attendee estimates before submitting
    if (!this.validateAttendeeEstimate()) {
      return;
    }

    this.model.setEventDateTimeUTC(this.meetingType.duration_hours);
    this.requestService.create(this.model, {
      company_id: this.getCurrentCompanyId()
    }).subscribe((response: any) => {
      this.toastr.success('Created!');
      this.model.id = response.id;
      this.navigate(this.router, this.returnUrl, this.returnUrlParams);
    }, err => {
      this.toastr.error(getErrorMessage(err, 'Failed to create!'));
    });
  }

  getTimezoneAbbr(timezone_id) {
    const timezone = this.timezones.find(t => t.id === timezone_id);
    return timezone ? timezone.abbr : '';
  }

  hasNonDedicatedQuestions(screen) {
    return this.dedicatedQuestionTypes && screen.questions.filter(q => !this.dedicatedQuestionTypes.includes(q.type_id)).length > 0;
  }

  clearAnswers(step, nextScreen) {
    if (nextScreen == 'END') {
      nextScreen = Object.keys(this.model.answers).length;
    }
    Object.keys(this.model.answers).forEach((key) => {
      const index = key.split('_')[0];
      if (index > step && index <= nextScreen) {
        delete this.model.answers[key];
      }
    });
  }
}
